import { FormControlElement, IFooter, ILanguage } from "../../../models/Interfaces";
import AppModal from "../../components/common/AppModal";
import { useEffect, useRef, useState } from "react";
import { createFormValidation } from "../../../helpers/formValidationHelper";
import { Core, formValidation, notEmpty } from "@form-validation/bundle/full";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import { Excluded } from '@form-validation/plugin-excluded';
import { faSave } from "@fortawesome/free-solid-svg-icons";
import { Col, Form, FormControl, FormGroup, FormLabel, FormSelect, Row, Tab, TabPane, Tabs } from "react-bootstrap";
import ColourPicker from "../../components/common/ColourPicker";
import FormCheckInput from "react-bootstrap/esm/FormCheckInput";
import HTMLEditor from "../../components/common/HTMLEditor";
import { buildFooterEditorFrame } from "../../components/common/editorSiteFrame";

interface IAddUpdateFooterModal {
  footer: IFooter;
  onClose: () => void;
  onSave: (footer: IFooter) => void;
  languages: Array<ILanguage>;
};

const FooterTabs = {
  General: "general",
  LeftColumn: "styledL",
  CentreColumn: "styledC",
  RightColumn: "styledR",
  Copyright: "copyright"
};

const Containers = [
  { id: "container", description: "middle container" },
  { id: "container-fluid", description: "full width container" }
];



const AddUpdateFooterModal = (props: IAddUpdateFooterModal) => {
  const [selectedFooter, setSelectedFooter] = useState<IFooter>();
  const [activeTab, setActiveTab] = useState<string>(FooterTabs.General);
  
  const formRef = useRef<HTMLFormElement | null>(null);
  const fvRef = useRef<Core | null>(null);

  const saveFooter = async () => {
    if (selectedFooter && fvRef.current) {
      const validres = await fvRef.current.validate();

      if (validres === "Valid") {
        props.onSave(selectedFooter);
      }
    }
  };

  const handleInputChange = (event: React.ChangeEvent<FormControlElement>) => {
    const { value, name } = event.target;

    if (name) {
      setSelectedFooter(prev => ({
        ...prev as IFooter,
        [name]: value
      }));
    }
  };

  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    if (name) {
      setSelectedFooter(prev => ({
        ...prev as IFooter,
        [name]: checked
      }));
    }
  };

  const createValidator = () => {
    if (!fvRef.current && formRef.current) {
      fvRef.current = createFormValidation(formRef.current, {
        fields: {
          Title: {
            validators: {
              notEmpty: { message: "footer name is required" }
            }
          },
          Language: {
            validators: {
              notEmpty: { message: "Please select a language for the footer" }
            }
          },
          ContainerClass: {
            validators: {
              notEmpty: { message: "Please select the container for the footer" }
            }
          }
        },
        plugins: {
          bootstrap5: new Bootstrap5(),
          trigger: new Trigger(),
          excluded: new Excluded(),          
        }
      });
    }
  };

  useEffect(() => {
    setSelectedFooter(props.footer);
  }, [props.footer])

  useEffect(() => {
    if (formRef.current && !fvRef.current) {
      createValidator();
    }
  }, []);

  return (
    <AppModal
      onClose={props.onClose}
      onDataAction={saveFooter}
      size="xl"
      showButtons={true}
      dataActionButtonText="Save Footer"
      title="Footer Details"
      actionButtonIcon={faSave}
      actionButtonVariant="success"
      cancelButtonVariant="primary">
        <Form id="AddUpdateFooterForm" ref={formRef}>
          <Tabs activeKey={activeTab}
            onSelect={(tk) => setActiveTab(tk ?? FooterTabs.General)}>

            <Tab eventKey={FooterTabs.General} title="General">
              <TabPane active={activeTab === FooterTabs.General}>
                <Row>
                  <Col md={12} xl={6} className="mt-3">
                    <FormLabel htmlFor="Language">Language</FormLabel>
                    <FormSelect
                      id="Language"
                      name="Language"
                      value={selectedFooter?.Language ?? ""}
                      onChange={handleInputChange}
                      title="Select language for footer">
                        <option value="">Select language...</option>
                        { props.languages.map(lang => (
                            <option value={`${lang.ShortCode}`} key={`LK-${lang.ShortCode}`}>{lang.Language}</option>
                          ))
                        }
                    </FormSelect>
                  </Col>
                </Row>
                <Row>
                  <Col md={12} xl={6} className="mt-3">
                    <FormLabel>Name</FormLabel>
                    <FormControl
                      type="text"
                      id="Title"
                      name="Title"
                      value={selectedFooter?.Title ?? ""}
                      onChange={handleInputChange}
                      placeholder="footer name...">
                    </FormControl>
                  </Col>
                  <Col md={12} xl={6} className="mt-3">
                    <FormLabel htmlFor="ContainerClass">Container Class</FormLabel>
                    <FormSelect
                      name="ContainerClass"
                      id="ContainerClass"
                      title="select the container for the content"
                      value={selectedFooter?.ContainerClass ?? ""}
                      onChange={handleInputChange}>
                        <option value="">Select the container for the footer...</option>
                        {
                          Containers.map(container => 
                            <option value={container.id} key={`CONT-${container.id}`}>{container.description}</option>
                          )
                        }
                    </FormSelect>
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} xl={4} className="mt-3">
                    <FormLabel htmlFor="BackgroundColour">Background Colour</FormLabel>
                    <ColourPicker 
                      id="BackgroundColour"
                      name="BackgroundColour"
                      onInputChange={handleInputChange}
                      selectedColour={selectedFooter?.BackgroundColour}
                    />
                  </Col>
                  <Col sm={12} xl={4} className="mt-3">
                    <FormLabel htmlFor="TextColour">Text Colour</FormLabel>
                    <ColourPicker
                      id="TextColour"
                      name="TextColour"
                      onInputChange={handleInputChange}
                      selectedColour={selectedFooter?.TextColour ?? ""}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={12} xl={4} className="mt-3">
                    <FormLabel htmlFor="ShowSocialMedia">Show social media items</FormLabel>
                    <FormCheckInput
                      className="form-check"
                      id="ShowSocialMedia"
                      name="ShowSocialMedia"
                      checked={selectedFooter?.ShowSocialMedia ?? false}
                      onChange={handleCheckChange}>
                    </FormCheckInput>
                  </Col>
                </Row>
              </TabPane>
            </Tab>

            <Tab eventKey={FooterTabs.LeftColumn} title="Left Column">
              <TabPane active={activeTab === FooterTabs.LeftColumn}>
                <Row>
                  <Col md={12} xl={4} className="mt-3">
                    <FormLabel htmlFor="Column1BackgroundColour">Background Colour</FormLabel>
                    <ColourPicker
                      id="Column1BackgroundColour"
                      name="Column1BackgroundColour"
                      selectedColour={selectedFooter?.Column1BackgroundColour ?? ""}
                      onInputChange={handleInputChange}
                    />
                  </Col>
                  <Col md={12} xl={4} className="mt-3">
                    <FormLabel htmlFor="Column1TextColour">Text Colour</FormLabel>
                    <ColourPicker
                      id="Column1TextColour"
                      name="Column1TextColour"
                      selectedColour={selectedFooter?.Column1TextColour ?? ""}
                      onInputChange={handleInputChange}
                    />
                  </Col>                  
                </Row>
                <Row>
                  <Col md={12} className="mt-3">
                    <FormLabel htmlFor="StyledColumn1">Footer Left Column Content (optional)</FormLabel>
                    <HTMLEditor documentKey={selectedFooter?.Id}
                      id="StyledColumn1"
                      name="StyledColumn1"
                      value={selectedFooter?.StyledColumn1 ?? ""}
                      onInputChange={handleInputChange}
                      siteFrame={buildFooterEditorFrame(selectedFooter ?? props.footer, "StyledColumn1")}
                    />
                  </Col>
                </Row>
              </TabPane>
            </Tab>

            <Tab eventKey={FooterTabs.CentreColumn} title="Centre Column">
              <TabPane active={activeTab === FooterTabs.CentreColumn}>
                <Row>
                  <Col md={12} xl={4} className="mt-3">
                    <FormLabel htmlFor="Column2BackgroundColour">Background Colour</FormLabel>
                    <ColourPicker
                      id="Column2BackgroundColour"
                      name="Column2BackgroundColour"
                      selectedColour={selectedFooter?.Column2BackgroundColour ?? ""}
                      onInputChange={handleInputChange}
                    />
                  </Col>
                  <Col md={12} xl={4} className="mt-3">
                    <FormLabel htmlFor="Column2TextColour">Text Colour</FormLabel>
                    <ColourPicker
                      id="Column2TextColour"
                      name="Column2TextColour"
                      selectedColour={selectedFooter?.Column2TextColour ?? ""}
                      onInputChange={handleInputChange}
                    />
                  </Col>                  
                </Row>
                <Row>
                  <Col md={12} className="mt-3">
                    <FormLabel htmlFor="StyledColumn2">Footer Middle Column Content (optional)</FormLabel>
                    <HTMLEditor documentKey={selectedFooter?.Id}
                      id="StyledColumn2"
                      name="StyledColumn2"
                      value={selectedFooter?.StyledColumn2 ?? ""}
                      onInputChange={handleInputChange}
                      siteFrame={buildFooterEditorFrame(selectedFooter ?? props.footer, "StyledColumn2")}
                    />
                  </Col>
                </Row>
              </TabPane>              
            </Tab>

            <Tab eventKey={FooterTabs.RightColumn} title="Right Column">
              <TabPane active={activeTab === FooterTabs.RightColumn}>
                <Row>
                  <Col md={12} xl={4} className="mt-3">
                    <FormLabel htmlFor="Column3BackgroundColour">Background Colour</FormLabel>
                    <ColourPicker
                      id="Column3BackgroundColour"
                      name="Column3BackgroundColour"
                      selectedColour={selectedFooter?.Column3BackgroundColour ?? ""}
                      onInputChange={handleInputChange}
                    />
                  </Col>
                  <Col md={12} xl={4} className="mt-3">
                    <FormLabel htmlFor="Column3TextColour">Text Colour</FormLabel>
                    <ColourPicker
                      id="Column3TextColour"
                      name="Column3TextColour"
                      selectedColour={selectedFooter?.Column3TextColour ?? ""}
                      onInputChange={handleInputChange}
                    />
                  </Col>                  
                </Row>
                <Row>
                  <Col md={12} className="mt-3">
                    <FormLabel htmlFor="StyledColumn3">Footer Right Column Content (optional)</FormLabel>
                    <HTMLEditor documentKey={selectedFooter?.Id}
                      id="StyledColumn3"
                      name="StyledColumn3"
                      value={selectedFooter?.StyledColumn3 ?? ""}
                      onInputChange={handleInputChange}
                      siteFrame={buildFooterEditorFrame(selectedFooter ?? props.footer, "StyledColumn3")}
                    />
                  </Col>
                </Row>
              </TabPane>              
            </Tab>

            <Tab eventKey={FooterTabs.Copyright} title="Full Width Content">
              <TabPane active={activeTab === FooterTabs.Copyright}>
                <Row>
                  <Col md={12} xl={4} className="mt-3">
                    <FormLabel htmlFor="CopyrightBackgroundColour">Background Colour</FormLabel>
                    <ColourPicker
                      id="CopyrightBackgroundColour"
                      name="CopyrightBackgroundColour"
                      selectedColour={selectedFooter?.CopyrightBackgroundColour ?? ""}
                      onInputChange={handleInputChange}
                    />
                  </Col>
                  <Col md={12} xl={4} className="mt-3">
                    <FormLabel htmlFor="CopyrightTextColour">Text Colour</FormLabel>
                    <ColourPicker
                      id="CopyrightTextColour"
                      name="CopyrightTextColour"
                      selectedColour={selectedFooter?.CopyrightTextColour ?? ""}
                      onInputChange={handleInputChange}
                    />
                  </Col>                  
                </Row>
                <Row>
                  <Col md={12} className="mt-3">
                    <FormLabel htmlFor="CopyrightNotice">Footer Full Width Content (optional)</FormLabel>
                    <HTMLEditor documentKey={selectedFooter?.Id}
                      id="CopyrightNotice"
                      name="CopyrightNotice"
                      value={selectedFooter?.CopyrightNotice ?? ""}
                      onInputChange={handleInputChange}
                      siteFrame={buildFooterEditorFrame(selectedFooter ?? props.footer, "CopyrightNotice")}
                    />
                  </Col>
                </Row>
              </TabPane>
            </Tab>
                              
          </Tabs>
        </Form>
    </AppModal>
  );
}

export default AddUpdateFooterModal;
